/**
 * Client → Data → Custom Metrics: the client's custom metrics and the
 * agency's "All clients" ones, with Create, Edit and Delete.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { EmptyFeature } from '../agency/EmptyFeature.tsx';
import type { Catalogue, Client } from '../reports/reports-api.ts';
import { ConfirmDelete } from './ConfirmDelete.tsx';
import { customApi, CUSTOM_SOURCE, VIEW_PREFIX, type CustomMetric } from './custom-api.ts';
import { CustomMetricDialog } from './CustomMetricDialog.tsx';
import { formulaText } from './FormulaEditor.tsx';

const TYPE_LABEL: Record<CustomMetric['data_type'], string> = {
  INTEGER: 'Integer',
  FLOAT: 'Float',
  CURRENCY: 'Currency',
  PERCENT: 'Percent',
};

export function CustomMetricsPanel(props: {
  organisationId: string;
  client: Client;
  catalogue: Catalogue;
  onChanged: () => void;
}): ReactNode {
  const { organisationId, client } = props;
  const [metrics, setMetrics] = useState<CustomMetric[] | null>(null);
  const [agency, setAgency] = useState<CustomMetric[]>([]);
  const [editing, setEditing] = useState<CustomMetric | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CustomMetric | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const sources = props.catalogue.sources.filter(
    (source) => source.key !== CUSTOM_SOURCE && !source.key.startsWith(VIEW_PREFIX),
  );
  const load = () => {
    customApi.metrics(organisationId, client.id).then(setMetrics, () => setMetrics([]));
    customApi.metrics(organisationId, null).then(setAgency, () => setAgency([]));
  };
  useEffect(load, [organisationId, client.id]);
  const changed = () => {
    setEditing(null);
    load();
    props.onChanged();
  };

  return (
    <div className="cx-panel">
      {problem && (
        <p className="rb-banner" role="alert">
          {problem}
        </p>
      )}
      {metrics?.length === 0 ? (
        <EmptyFeature
          title="Custom Metrics"
          lead="Build your own metric from the ones you connect, for widgets, KPIs and AgencyAI to reason over."
          points={[
            'Divide, multiply, add or subtract any connected metrics',
            'Blend sources: total ad spend across platforms, cost per lead',
            'Use it in this client only, or in every client',
          ]}
          action={{ label: 'Create Custom Metric', onClick: () => setEditing('new') }}
        />
      ) : (
        <div className="ag-table-card">
          <div className="cx-head">
            <p className="ag-table-meta">
              {metrics === null ? 'Loading…' : `Showing ${metrics.length} Rows`}
            </p>
            <button type="button" className="btn" onClick={() => setEditing('new')}>
              Create Custom Metric
            </button>
          </div>
          <table className="ag-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Formula</th>
                <th scope="col">Data Type</th>
                <th scope="col">Available to</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(metrics ?? []).map((metric) => (
                <tr key={metric.id}>
                  <td>
                    <strong>{metric.name}</strong>
                    {metric.description && (
                      <small className="ag-cell-note">{metric.description}</small>
                    )}
                  </td>
                  <td className="cx-formula">
                    {formulaText(metric.formula, { kind: 'metrics', sources })}
                  </td>
                  <td>{TYPE_LABEL[metric.data_type]}</td>
                  <td>{metric.business_unit_id ? client.name : 'All clients'}</td>
                  <td className="cx-actions">
                    <button type="button" className="ex-link" onClick={() => setEditing(metric)}>
                      Edit
                    </button>
                    <button type="button" className="ex-link" onClick={() => setDeleting(metric)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <CustomMetricDialog
          organisationId={organisationId}
          clientId={client.id}
          clientName={client.name}
          sources={sources}
          metric={editing === 'new' ? null : editing}
          others={agency.filter((metric) => metric.business_unit_id !== client.id)}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      {deleting && (
        <ConfirmDelete
          name={deleting.name}
          note="Widgets and KPIs that use it will say the metric is gone."
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const target = deleting;
            setDeleting(null);
            customApi
              .removeMetric(organisationId, target.id)
              .then(changed, (error: unknown) =>
                setProblem(error instanceof Error ? error.message : 'It could not be deleted.'),
              );
          }}
        />
      )}
    </div>
  );
}
